import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { DEMO_DATASETS } from '@/data/demoDatasets';
import { useActions } from '@/store/selectors';

interface LoadDemoModalProps {
  open: boolean;
  onClose: () => void;
}

export function LoadDemoModal({ open, onClose }: LoadDemoModalProps): JSX.Element | null {
  const { loadDemoDataset } = useActions();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSelect = (index: number): void => {
    loadDemoDataset(DEMO_DATASETS[index]);
    onClose();
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="load-demo-title"
    >
      <div
        className="modal load-demo-modal"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="load-demo-title" className="modal-title">Load Demo Dataset</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body">
          <p className="hint-text mt-3">
            Select a pre-configured patient scenario to explore the calculator.
            This replaces the current patient data.
          </p>
          <ul className="demo-dataset-list" role="listbox" aria-label="Demo datasets">
            {DEMO_DATASETS.map((ds, i) => (
              <li key={ds.name} role="option" aria-selected="false">
                <button
                  type="button"
                  className="demo-dataset-item"
                  onClick={() => handleSelect(i)}
                  data-testid={`demo-dataset-${i}`}
                >
                  <span className="demo-dataset-name">{ds.name}</span>
                  <span className="demo-dataset-desc">{ds.description}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
